import React, { useEffect, useMemo, useState } from 'react';
import useComponentVisible from '../UI/ComponentVisible';
import dotsImgUrl from '../../assets/three-dots-vertical.svg';
import { useUserContext } from '../../contexts/UserContext';
import { Search, Plus, Pencil, Trash2, Tag, Calendar } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import AgTableCard from '../UI/AgTableCard';
import ReleaseModal from './ReleaseModal';
import DeleteReleaseModal from './DeleteReleaseModal';
import { formatDate } from '../../utils/converter';
import { getAllReleases } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import AgGridWrapper from '../UI/AgGrid';

const ManageRelease = () => {
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);
    const { content } = useContentfulContext();

    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [loading, setLoading] = useState(false);
    const [selectedAction, setSelectedAction] = useState('');
    const [release, setRelease] = useState([]);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const { userData } = useUserContext();
    const [gridApi, setGridApi] = useState(null);

    const fetchRelease = async () => {
        try {
            setLoading(true);
            const res = await getAllReleases(userData?.token, userData?.selectedOrganization?.org_token);
            if (res.status) {
                setRelease(res.data);
            }
        } catch (error) {
            console.error("Error fetching releases:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRelease();
    }, []);

    const colDefs = [
        {
            headerName: content.ADMIN_RELEASE.table_headers.version,
            field: "version",
            sortable: true,
            filter: true,
            cellRenderer: ({ data }) => (
                <div className="flex items-center gap-2 h-full">
                    <Tag size={14} className="text-stride-muted shrink-0" />
                    <button
                        className="font-semibold text-stride-primary hover:text-stride-primary-hover hover:underline transition-colors"
                        onClick={() => {
                            setSelectedRelease(data);
                            setSelectedAction("edit");
                        }}
                    >
                        {data?.version}
                    </button>
                </div>
            ),
            suppressMovable: true,
            minWidth: 160,
        },
        {
            headerName: content.ADMIN_RELEASE.table_headers.status,
            field: "status",
            sortable: true,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                const status = (data?.status || "").toLowerCase();
                let pillClass = "stride-badge-pill-neutral";
                if (status === "published" || status === "active" || status === "live") {
                    pillClass = "stride-badge-pill-success";
                } else if (status === "draft" || status === "in_progress") {
                    pillClass = "stride-badge-pill-warning";
                } else if (status === "scheduled") {
                    pillClass = "stride-badge-pill-info";
                }
                return (
                    <div className="flex items-center h-full">
                        <span className={`stride-badge-pill ${pillClass} capitalize`}>
                            {data?.status || "Draft"}
                        </span>
                    </div>
                );
            },
            minWidth: 140,
        },
        {
            headerName: content.ADMIN_RELEASE.table_headers.release_date,
            field: "release_date",
            sortable: true,
            filter: true,
            cellRenderer: ({ data }) => (
                <div className="flex items-center gap-1.5 h-full text-xs text-stride-body">
                    <Calendar size={13} className="text-stride-muted shrink-0" />
                    <span>{data?.release_date ? formatDate(data?.release_date) : "—"}</span>
                </div>
            ),
            suppressMovable: true,
            minWidth: 180,
        },
        {
            headerName: content.ADMIN_RELEASE.table_headers.created_at,
            field: "createdAt",
            sortable: true,
            cellRenderer: ({ data }) => (
                <span className="text-xs text-stride-muted">
                    {data?.createdAt ? formatDate(data?.createdAt) : "—"}
                </span>
            ),
            suppressMovable: true,
            minWidth: 160,
        },
        {
            headerName: "Actions",
            field: "actions",
            cellRenderer: ({ data }) => (
                <div className="flex items-center justify-end gap-1.5 h-full">
                    {/* Direct tactile buttons */}
                    <button
                        type="button"
                        className="stride-table-btn stride-table-btn-edit"
                        title="Edit Release"
                        onClick={() => {
                            setSelectedRelease(data);
                            setSelectedAction("edit");
                        }}
                    >
                        <Pencil size={13} />
                    </button>
                    <button
                        type="button"
                        className="stride-table-btn stride-table-btn-danger"
                        title="Delete Release"
                        onClick={() => {
                            setSelectedRelease(data);
                            setSelectedAction("delete");
                        }}
                    >
                        <Trash2 size={13} />
                    </button>

                    {/* Backwards compatibility 3-dots */}
                    <button
                        type="button"
                        aria-label="Actions"
                        className="actions stride-table-btn stride-table-btn-secondary"
                        onClick={(e) => {
                            setSelectedRelease(data);
                            setIsComponentVisible(!isComponentVisible);
                            const buttonRect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({
                                top: buttonRect.bottom + window.scrollY,
                                left: buttonRect.left + window.scrollX,
                            });
                        }}
                    >
                        <img src={dotsImgUrl} alt="Actions" className="w-3.5 h-3.5 opacity-70" />
                    </button>
                </div>
            ),
            width: 140,
            minWidth: 140,
            sortable: false,
        },
    ];

    const autoSizeStrategy = useMemo(() => ({
        type: "fitGridWidth",
        defaultMinWidth: 120,
        columnLimits: [
            { colId: "version", minWidth: 160 },
            { colId: "status", minWidth: 140 },
            { colId: "release_date", minWidth: 180 },
            { colId: "createdAt", minWidth: 160 },
            { colId: "actions", minWidth: 140 }
        ],
    }), []);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = event.target.value.toLowerCase();
        if (gridApi) {
            gridApi.setFilterModel({
                version: { type: 'contains', filter: filterValue }
            });
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    return (
        <>
            <div className="stride-page-shell">
            <main className="w-full space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-stride-muted mb-1">
                            System Administration
                        </p>
                        <h1 className="text-2xl font-semibold tracking-tight text-stride-heading">
                            {content.ADMIN_RELEASE.title || "Manage Releases"}
                        </h1>
                        <p className="text-sm text-stride-body mt-0.5">
                            Track product releases, pre/post notification triggers, and version lifecycles.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 self-start sm:self-auto">
                        <Button
                            variant="primary"
                            icon={<Plus size={16} />}
                            onClick={() => {
                                setSelectedAction("create");
                                setSelectedRelease(null);
                            }}
                        >
                            {content.ADMIN_RELEASE.cta_text.create_release || "Create Release"}
                        </Button>
                    </div>
                </div>

                {/* Table Card with Integrated Action Bar Header */}
                <AgTableCard
                    header={
                        <div className="w-full sm:w-80">
                            <Input
                                type="text"
                                placeholder="Search by version..."
                                prefixIcon={<Search size={16} />}
                                onChange={onFilterTextBoxChanged}
                            />
                        </div>
                    }
                    tableId="manage-release-table"
                    loading={loading}
                    rowData={release}
                    columnDefs={colDefs}
                    autoSizeStrategy={autoSizeStrategy}
                    onGridReady={onGridReady}
                    domLayout="autoHeight"
                />

                    {/* Dropdown Menu for backwards compatibility & 3-dots */}
                    {isComponentVisible && selectedRelease && (
                        <div
                            ref={ref}
                            className="absolute z-10 w-48 rounded-lg bg-white shadow-lg border border-stride-border py-1 ring-1 ring-black ring-opacity-5 animate-in fade-in zoom-in-95 duration-100"
                            style={{ top: menuPosition.top, left: Math.max(16, menuPosition.left - 180) }}
                        >
                            <a
                                href="#edit"
                                className="block px-4 py-2 text-sm text-stride-heading hover:bg-stride-hover transition-colors"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsComponentVisible(false);
                                    setSelectedAction('edit');
                                }}
                            >
                                {content.ADMIN_RELEASE.more_info.edit || "Edit"}
                            </a>
                            <a
                                href="#delete"
                                className="block px-4 py-2 text-sm text-stride-danger hover:bg-stride-danger/10 transition-colors"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsComponentVisible(false);
                                    setSelectedAction('delete');
                                }}
                            >
                                {content.ADMIN_RELEASE.more_info.delete || "Delete"}
                            </a>
                        </div>
                    )}
                </main>

                {selectedAction === "create" && (
                    <ReleaseModal setSelectedAction={setSelectedAction} getRelease={fetchRelease} />
                )}
                {selectedAction === "edit" && (
                    <ReleaseModal setSelectedAction={setSelectedAction} selectedRelease={selectedRelease} getRelease={fetchRelease} />
                )}
                {selectedAction === "delete" && (
                    <DeleteReleaseModal
                        setSelectedAction={setSelectedAction}
                        selectedRelease={selectedRelease}
                        setSelectedRelease={setSelectedRelease}
                        getRelease={fetchRelease}
                    />
                )}
            </div>
        </>
    );
};

export default ManageRelease;
